import { parseJsonObject, readObject, readString } from './json';

/**
 * The training wire - port of `TrainingChannelMessages.cs`, with the scenario
 * shape the state machine package (`com.ethar.trainingstatemachine`, C# and
 * Python) serialises. The server's `training_flow.py` `_emit` is the wire
 * truth for events; the scenario JSON files under
 * `QuestVisionStreamServer/training-scenarios/` are the truth for steps.
 *
 * Client -> authority: `trainingAction` (a form action's result pair),
 * `trainingControl` (begin/reset/sync), `trainingScenario` (push a scenario).
 * Authority -> observers: `training` events (loaded/step/completed/state),
 * each carrying a full machine-state snapshot so mirroring is idempotent.
 * Additive wire rule throughout: unknown types/fields are ignored.
 */

/** "present" - the state a class enters the registry with on first sighting. */
export const TRAINING_STATE_PRESENT = 'present';

export interface TrainingStep {
  readonly waitingClass: string;
  readonly waitingState: string;
  readonly detectedClass: string;
  readonly label: string;
  readonly title: string;
  readonly description: string;
  readonly options: readonly string[];
  readonly imageRef: string;
  readonly modelRef: string;
  readonly result: string;
  readonly resultState: string;
}

export interface TrainingScenario {
  readonly name: string;
  readonly steps: readonly TrainingStep[];
}

/** A step with no title and no options is a pass-through: no form is shown. */
export function stepHasPresentation(step: TrainingStep): boolean {
  return step.title.length > 0 || step.options.length > 0;
}

/** The step labels a detected class in the world. */
export function stepHasWorldLabel(step: TrainingStep): boolean {
  return step.detectedClass.length > 0 && step.label.length > 0;
}

function readStringArray(root: Record<string, unknown>, key: string): string[] {
  const value = root[key];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function parseStep(root: Record<string, unknown>): TrainingStep {
  return {
    waitingClass: readString(root, 'waitingClass'),
    waitingState: readString(root, 'waitingState'),
    detectedClass: readString(root, 'detectedClass'),
    label: readString(root, 'label'),
    title: readString(root, 'title'),
    description: readString(root, 'description'),
    options: readStringArray(root, 'options'),
    imageRef: readString(root, 'imageRef'),
    modelRef: readString(root, 'modelRef'),
    result: readString(root, 'result'),
    resultState: readString(root, 'resultState'),
  };
}

/**
 * Parse a scenario object - either the bare `{ name, steps }` the wire embeds
 * or the configuration wrapper `{ scenario: { name, steps }, ... }` the files
 * use. Null without a name or a steps array.
 */
export function parseTrainingScenario(value: unknown): TrainingScenario | null {
  if (typeof value !== 'object' || value === null) return null;
  let root = value as Record<string, unknown>;
  const wrapped = readObject(root, 'scenario');
  if (wrapped) root = wrapped;
  const name = readString(root, 'name');
  const steps = root['steps'];
  if (!name || !Array.isArray(steps)) return null;
  return {
    name,
    steps: steps
      .filter((s): s is Record<string, unknown> => typeof s === 'object' && s !== null)
      .map(parseStep),
  };
}

/** Scenario -> wire object (the shape the C#/Python `scenario_to_json` emit). */
export function scenarioToWire(scenario: TrainingScenario): Record<string, unknown> {
  return {
    name: scenario.name,
    steps: scenario.steps.map((step) => ({ ...step, options: [...step.options] })),
  };
}

// ------------------------------------------------------------ client -> server

/** A training form action's result pair (Unity `TrainingStepResult`). */
export interface TrainingStepResult {
  readonly stepIndex: number;
  readonly waitingClass: string;
  readonly resultClass: string;
  readonly resultState: string;
  readonly optionLabel: string;
}

/** Build the result for a pressed option on a step (the presentation layer's rule). */
export function stepResult(stepIndex: number, step: TrainingStep, optionLabel: string): TrainingStepResult {
  return {
    stepIndex,
    waitingClass: step.waitingClass,
    resultClass: step.result,
    resultState: step.resultState,
    optionLabel,
  };
}

export function trainingActionJson(result: TrainingStepResult): string {
  return JSON.stringify({
    type: 'trainingAction',
    stepIndex: result.stepIndex,
    waitingClass: result.waitingClass,
    resultClass: result.resultClass,
    resultState: result.resultState,
    option: result.optionLabel,
  });
}

export type TrainingChannelCommand = 'begin' | 'reset' | 'sync';

export function trainingControlJson(command: TrainingChannelCommand): string {
  return JSON.stringify({ type: 'trainingControl', command });
}

export function trainingScenarioJson(scenario: TrainingScenario): string {
  return JSON.stringify({ type: 'trainingScenario', scenario: scenarioToWire(scenario) });
}

// ------------------------------------------------------------ server -> client

export type TrainingChannelEventKind = 'loaded' | 'step' | 'completed' | 'state';
export type TrainingFlowStatus = 'idle' | 'running' | 'completed';
export type TrainingClassSource = 'detection' | 'action' | 'apriltag';

/**
 * One parsed authority -> client training event: the event identity plus the
 * machine-state snapshot every event carries.
 */
export interface TrainingChannelEvent {
  readonly kind: TrainingChannelEventKind;
  /** The full scenario, when the event carries one (always on `loaded`; optional elsewhere). */
  readonly scenario: TrainingScenario | null;
  /** Total steps, on `step` events. */
  readonly stepCount: number;
  /** The pair that caused a step/completed event (empty on begin). */
  readonly arrivedClass: string;
  readonly arrivedState: string;
  readonly source: TrainingClassSource;
  // ---- the state snapshot (present on every event) ----
  readonly status: TrainingFlowStatus;
  /** Active step index, -1 when idle/completed. */
  readonly stepIndex: number;
  readonly expectedClass: string;
  readonly expectedState: string;
  readonly discardedCount: number;
  /** The authority's detection registry (class -> state). */
  readonly detectedObjects: ReadonlyMap<string, string>;
}

function parseStatus(value: string): TrainingFlowStatus {
  return value === 'running' || value === 'completed' ? value : 'idle';
}

function parseSource(value: string): TrainingClassSource {
  return value === 'action' || value === 'apriltag' ? value : 'detection';
}

/**
 * Parse a raw training-topic message as a training event. Null for anything
 * that isn't a `{"type":"training"}` message - cheap enough to probe every
 * message. A `loaded` event without a readable scenario is useless -> null.
 */
export function parseTrainingEvent(json: string): TrainingChannelEvent | null {
  const root = parseJsonObject(json);
  if (!root || readString(root, 'type') !== 'training') return null;

  const kindToken = readString(root, 'event');
  if (kindToken !== 'loaded' && kindToken !== 'step' && kindToken !== 'completed' && kindToken !== 'state') return null;

  const scenario = parseTrainingScenario(root['scenario']);
  if (kindToken === 'loaded' && !scenario) return null;

  const state = readObject(root, 'state');
  const detectedObjects = new Map<string, string>();
  const registry = state ? readObject(state, 'detectedObjects') : null;
  if (registry) {
    for (const [key, value] of Object.entries(registry)) {
      if (typeof value === 'string') detectedObjects.set(key.toLowerCase(), value);
    }
  }

  const stepCount = root['stepCount'];
  const stepIndex = state?.['stepIndex'];
  const discarded = state?.['discarded'];

  return {
    kind: kindToken,
    scenario,
    stepCount: typeof stepCount === 'number' && Number.isFinite(stepCount) ? stepCount : 0,
    arrivedClass: readString(root, 'arrivedClass'),
    arrivedState: readString(root, 'arrivedState'),
    source: parseSource(readString(root, 'source')),
    status: state ? parseStatus(readString(state, 'status')) : 'idle',
    stepIndex: typeof stepIndex === 'number' && Number.isFinite(stepIndex) ? stepIndex : -1,
    expectedClass: state ? readString(state, 'expectedClass') : '',
    expectedState: state ? readString(state, 'expectedState') : '',
    discardedCount: typeof discarded === 'number' && Number.isFinite(discarded) ? discarded : 0,
    detectedObjects,
  };
}
